import type { CSSProperties } from "react";

/**
 * The site's one card stack.
 *
 * It was written for the "Come funziona" hero, whose deck of real impianti
 * shuffles a card to the back every few seconds; the producer role pages then
 * wanted the same physical stack for their benefit cards, so the geometry moved
 * here and both read from it. Two consumers, two very different drivers, one CSS
 * transition per reshuffle there, a scroll-scrubbed anime.js timeline here, but
 * a card at depth 2 has to sit in the same place either way.
 *
 * Depth is the distance from the front of the stack: 0 is the card being read,
 * higher numbers step up and to the right, turn a little further and fade out.
 */
// Per depth: the card steps up-and-right, shrinks, turns a little further away
// (`tilt`) and, crucially, under the deck's `preserve-3d`, is pushed back along
// the viewer axis (`tz`). Without that push a turned card's near edge tips toward
// the viewer and paints in front of the straight front card; `tz` keeps the
// straight card genuinely closest and the turn growing with distance.
const STEP = { x: 40, y: -18, scale: 0, rot: 2, tilt: -4.5, tz: -60 };

/** Opacity per depth; anything past the end of the table is invisible. */
const FADE = [1, 0.9, 0.72, 0.48];

type DeckDepth = { x: number; y: number; z3d: number; scale: number; rot: number; rotateY: number; opacity: number; z: number };

/** Where a card sits, given its distance from the front. */
export function deckDepth(depth: number): DeckDepth {
  return {
    x: depth * STEP.x,
    y: depth * STEP.y,
    z3d: depth * STEP.tz, // pushed back along the viewer axis so it sits behind the front
    scale: 1 - depth * STEP.scale,
    rot: depth * STEP.rot,
    rotateY: depth * STEP.tilt,
    opacity: depth >= FADE.length ? 0 : FADE[depth],
    z: FADE.length - depth,
  };
}

/** The same as an inline style, what a stack whose reshuffle is a CSS transition sets. */
export function deckStyle(depth: number): CSSProperties {
  const d = deckDepth(depth);
  return {
    transform: `translateX(${d.x}px) translateY(${d.y}px) translateZ(${d.z3d}px) scale(${d.scale}) rotate(${d.rot}deg) rotateY(${d.rotateY}deg)`,
    opacity: d.opacity,
    zIndex: d.z,
  };
}
